import React, { createContext, useContext, useEffect, useState } from "react";
import { NewsData, NEWS_API_ENDPOINTS, defaultNewsData, DefaultImageList, defaultImageListData } from "./constants";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "../../utils/axiosInstance";
import { message } from "antd";

interface NewsContextState {
	newsData: NewsData;
	defaultImageList: DefaultImageList[];
	loadingGetNewsData?: boolean;
	loadingAddNewsData: boolean;
	loading: boolean;
	loadingEditNewsData: boolean;
	setNewsData: React.Dispatch<React.SetStateAction<NewsData>>;
	setDefaultImageList: React.Dispatch<React.SetStateAction<DefaultImageList[]>>;
	addNews: () => void;
	updateNews: () => void;
	clearNewsData: () => void;
	getNewsDetails: (id: string) => void;
}

interface NewsProviderProps {
	children: React.ReactNode;
}

const NewsContext = createContext<NewsContextState | undefined>(
	undefined
);

export const useNewsContext = () => {
	const context = useContext(NewsContext);
	if (!context) {
		throw new Error(
			"useNewsContext must be used within an NewsContextProvider"
		);
	}
	return context;
};

function isFileObject(value: any) {
	return value instanceof File;
}

export const EventsContextProvider: React.FC<NewsProviderProps> = ({
	children,
}) => {
	const navigate = useNavigate();
	const [newsData, setNewsData] = useState<NewsData>(defaultNewsData);
	const [defaultImageList, setDefaultImageList] = useState<DefaultImageList[]>(defaultImageListData);

	const [loadingAddNewsData, setLoadingAddNewsData] = useState(false);
	const [loadingEditNewsData, setLoadingEditNewsData] = useState(false);
	const [loadingGetNewsData, setLoadingGetNewsData] = useState(false);
	const [errorAddNewsData, setErrorAddNewsData] = useState<any>(undefined);

	const loading = loadingAddNewsData
	const error = errorAddNewsData

	const isNewsDataValid = (data: NewsData) => {
		if (!data.releaseDate || data.isVisible === undefined || data.displayOrder === undefined
			|| !data.eventDetail || data.eventDetail.length === 0) {
			return false;
		}

		for (const detail of data.eventDetail) {
			if (!detail.title || !detail.shorText || !detail.text || !detail.culture ||
				!detail.imageUrl || !detail.smallImageUrl) {
				return false;
			}
		}
		return true;
	}

	const clearNewsData = () => {
		setNewsData(defaultNewsData);
		setDefaultImageList(defaultImageListData);
	}

	const addNews = async () => {
		setLoadingAddNewsData(true);

		if (!isNewsDataValid(newsData)) {
			message.info("Lütfen tüm alanları doldurun.");
			setLoadingAddNewsData(false);
			return;
		}

		if (newsData.eventDetail[0].shorText?.length > 150) {
			message.info("Kısa metin 150 karakterden az olmalıdır.");
			setLoadingAddNewsData(false);
			return;
		}

		const formData = new FormData();
		formData.append('releaseDate', newsData.releaseDate);
		formData.append('isVisible', newsData.isVisible.toString());
		formData.append('displayOrder', newsData.displayOrder?.toString() ?? '');

		formData.append(`eventDetail[${0}].title`, newsData.eventDetail[0].title);
		formData.append(`eventDetail[${0}].shorText`, newsData.eventDetail[0].shorText);
		const styledText = newsData.eventDetail[0].text.replace(/<p>/g, `<p style="font-family: 'Roboto';font-style: normal;font-weight: 500;font-size: 16px;line-height: 29px;color: #e9e9e9;">`);
		formData.append(`eventDetail[${0}].text`, styledText);
		formData.append(`eventDetail[${0}].culture`, newsData.eventDetail[0].culture);
		formData.append(`title`, newsData.eventDetail[0].title);

		if (newsData.eventDetail[0].imageUrl) {
			formData.append(`eventDetail[${0}].imageUrl`, newsData.eventDetail[0].imageUrl);
		}
		if (newsData.eventDetail[0].smallImageUrl) {
			formData.append(`eventDetail[${0}].smallImageUrl`, newsData.eventDetail[0].smallImageUrl);
		}

		formData.append(`eventDetail[${1}].title`, newsData.eventDetail[0].title);
		formData.append(`eventDetail[${1}].shorText`, newsData.eventDetail[0].shorText);
		formData.append(`eventDetail[${1}].text`, styledText);
		formData.append(`eventDetail[${1}].culture`, "en-US");
		formData.append(`title`, newsData.eventDetail[0].title);

		if (newsData.eventDetail[0].imageUrl) {
			formData.append(`eventDetail[${1}].imageUrl`, newsData.eventDetail[0].imageUrl);
		}
		if (newsData.eventDetail[0].smallImageUrl) {
			formData.append(`eventDetail[${1}].smallImageUrl`, newsData.eventDetail[0].smallImageUrl);
		}


		try {
			const response = await axiosInstance.post(`${NEWS_API_ENDPOINTS.ADD_EVENT}`, formData, {
				headers: {
					'Content-Type': 'multipart/form-data'
				}
			});

			setLoadingAddNewsData(false);

			if (response.data && response.data.result.isSuccessful) {
				message.success("Etkinlik başarıyla eklendi.");
				setNewsData(defaultNewsData);
				navigate("/events");
			} else {
				message.error(response.data.result.error.message);
			}
		} catch (error) {
			console.error(error);
			setErrorAddNewsData(error);
			setLoadingAddNewsData(false);
		}
	};

	const updateNews = async () => {
		setLoadingEditNewsData(true);

		if (!isNewsDataValid(newsData)) {
			message.info("Lütfen tüm alanları doldurun.");
			setLoadingEditNewsData(false);
			return;
		}
		if (newsData.eventDetail[0].shorText?.length > 150) {
			message.info("Kısa metin 150 karakterden az olmalıdır.");
			setLoadingEditNewsData(false);
			return;
		} 
		const formData = new FormData();
		formData.append('releaseDate', newsData.releaseDate);
		formData.append('isVisible', newsData.isVisible.toString());
		formData.append('displayOrder', newsData.displayOrder?.toString() ?? '');
		formData.append('eventId', newsData.eventId?.toString() ?? '');

		formData.append(`eventDetail[${0}].title`, newsData.eventDetail[0].title);
		formData.append(`eventDetail[${0}].shorText`, newsData.eventDetail[0].shorText);
		let styledText = newsData.eventDetail[0].text.replace(/<p>/g, `<p style="font-family: 'Roboto';font-style: normal;font-weight: 500;font-size: 16px;line-height: 29px;color: #e9e9e9;">`);
		styledText = styledText.replace(/<a /g, `<a style="color: #e9e9e9;" `);
		formData.append(`eventDetail[${0}].text`, styledText);
		formData.append(`eventDetail[${0}].culture`, newsData.eventDetail[0].culture);
		formData.append(`title`, newsData.eventDetail[0].title);

		if (!isFileObject(newsData.eventDetail[0].imageUrl)) {
			newsData.eventDetail[0].imageUrl = null;
		}
		if (!isFileObject(newsData.eventDetail[0].smallImageUrl)) {
			newsData.eventDetail[0].smallImageUrl = null;
		}

		if (newsData.eventDetail[0].imageUrl) {
			formData.append(`eventDetail[${0}].imageUrl`, newsData.eventDetail[0].imageUrl);
		}
		if (newsData.eventDetail[0].smallImageUrl) {
			formData.append(`eventDetail[${0}].smallImageUrl`, newsData.eventDetail[0].smallImageUrl);
		}

		formData.append(`eventDetail[${1}].title`, newsData.eventDetail[0].title);
		formData.append(`eventDetail[${1}].shorText`, newsData.eventDetail[0].shorText);
		formData.append(`eventDetail[${1}].text`, styledText);
		formData.append(`eventDetail[${1}].culture`, "en-US");
		formData.append(`title`, newsData.eventDetail[0].title);

		if (!isFileObject(newsData.eventDetail[0].imageUrl)) {
			newsData.eventDetail[0].imageUrl = null;
		}
		if (!isFileObject(newsData.eventDetail[0].smallImageUrl)) {
			newsData.eventDetail[0].smallImageUrl = null;
		}

		if (newsData.eventDetail[0].imageUrl) {
			formData.append(`eventDetail[${1}].imageUrl`, newsData.eventDetail[0].imageUrl);
		}
		if (newsData.eventDetail[0].smallImageUrl) {
			formData.append(`eventDetail[${1}].smallImageUrl`, newsData.eventDetail[0].smallImageUrl);
		}

		try {
			const response = await axiosInstance.put(`${NEWS_API_ENDPOINTS.UPDATE_EVENT}`, formData, {
				headers: {
					'Content-Type': 'multipart/form-data'
				}
			});

			setLoadingEditNewsData(false);

			if (response.data && response.data.result.isSuccessful) {
				message.success("Etkinlik başarıyla güncellendi.");
				setNewsData(defaultNewsData);
				navigate("/events");
			} else {
				message.error(response.data.result.error.message);
			}
		} catch (error) {
			console.error(error);
			setErrorAddNewsData(error);
			setLoadingEditNewsData(false);
		}
	};

	const getNewsDetails = async (id: string) => {
		setLoadingGetNewsData(true);
		try {
			const response = await axiosInstance.get(`${NEWS_API_ENDPOINTS.GET_EVENT_DETAILS}?eventId=${id}`);
			if (response.data && response.data.isSuccessful) {
				setNewsData(response?.data?.result);
				setDefaultImageList(response?.data?.result?.eventDetail)
			}
			setLoadingGetNewsData(false);
		} catch (error) {
			console.error(error);
			setErrorAddNewsData(error);
			setLoadingGetNewsData(false);
		}
	};

	const value: NewsContextState = {
		newsData,
		defaultImageList,
		loadingGetNewsData,
		loadingAddNewsData,
		loadingEditNewsData,
		loading,
		setNewsData,
		setDefaultImageList,
		addNews,
		clearNewsData,
		updateNews,
		getNewsDetails,
	};

	return (
		<NewsContext.Provider value={value}>
			{children}
		</NewsContext.Provider>
	);
};
